import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { fetchProviders } from "src/actions/callrecorder/providersActions";
import { selectProviderIds, selectProviderEntities } from "src/store/callrecorder/providersSlice";
import { createCsvImport } from "src/actions/callrecorder/csvImportsActions";
import { objToFormData } from "src/helpers/FormHelper";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Alert, Button, Form, Radio } from "antd";
import { CloudUploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { RcFile } from "antd/es/upload";

interface InlineUploadFormProps {
  importType: string;
}

const _InlineUploadForm = ({ importType }: InlineUploadFormProps) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const providerIds = useAppSelector(selectProviderIds);
  const providerEntities = useAppSelector(selectProviderEntities);
  const [file, setFile] = useState<RcFile | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchProviders({ limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    form.setFieldsValue({ import_type: importType });
  }, [importType, form]);

  const providers = providerIds.map(id => providerEntities[id]).filter(Boolean);

  useEffect(() => {
    if (providers.length > 0 && !form.getFieldValue('provider_id')) {
      form.setFieldsValue({ provider_id: providers[0]!.id });
    }
  }, [providerIds]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile as RcFile);
      form.setFieldsValue({ csv_file: droppedFile });
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile as RcFile);
      form.setFieldsValue({ csv_file: selectedFile });
    }
  }

  function handleClickDropzone() {
    const input = document.getElementById('csv-file-input') as HTMLInputElement;
    input?.click();
  }

  function handleRemoveFile(e: React.MouseEvent) {
    e.stopPropagation();
    setFile(null);
    form.setFieldsValue({ csv_file: undefined });
  }

  async function handleFinish(values: any) {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError(null);

    const formData = objToFormData({ ...values, csv_file: file });
    const resultAction = await dispatch(createCsvImport(formData));

    if (createCsvImport.fulfilled.match(resultAction)) {
      const csvImport = resultAction.payload.csv_import;
      const id = Object.keys(csvImport)[0];
      navigate(callrecorderUrls.csvImportPath(Number(id)));
    } else {
      const payload = resultAction.payload;
      const errorMsg = Array.isArray(payload) ? payload[0] : (payload as any)?.message || 'Upload failed';
      setError(errorMsg);
    }

    setUploading(false);
  }

  return (
    <div className="v-inline-upload">
      <div className="v-inline-upload__header">
        <h3 className="v-inline-upload__title">Upload a CSV file</h3>
        <p className="v-inline-upload__subtitle">
          Make sure file includes the correct columns for {importType === 'phone_number_price' ? 'phone number prices' : 'rates'} and <strong>select correct provider</strong>
        </p>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          className="v-inline-upload__alert"
        />
      )}

      <Form form={form} onFinish={handleFinish} layout="vertical" className="v-inline-upload__form">
        <Form.Item name="import_type" hidden>
          <input type="hidden" />
        </Form.Item>

        <Form.Item
          name="provider_id"
          rules={[{ required: true, message: 'Provider is required' }]}
        >
          <Radio.Group className="v-inline-upload__providers">
            {providers.map(provider => (
              <Radio key={provider!.id} value={provider!.id}>
                {provider!.name}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <div
          className={`v-inline-upload__dropzone ${dragOver ? 'v-inline-upload__dropzone--active' : ''} ${file ? 'v-inline-upload__dropzone--has-file' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClickDropzone}
        >
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {file ? (
            <div className="v-inline-upload__file-info">
              <CloudUploadOutlined className="v-inline-upload__icon v-inline-upload__icon--success" />
              <p className="v-inline-upload__filename">{file.name}</p>
              <button type="button" className="v-inline-upload__remove" onClick={handleRemoveFile}>
                Remove
              </button>
            </div>
          ) : (
            <>
              <CloudUploadOutlined className="v-inline-upload__icon" />
              <p className="v-inline-upload__drop-text">Drop a file or click to browse</p>
              <p className="v-inline-upload__drop-hint">File with up to 10.000 rows works best</p>
            </>
          )}
        </div>

        <div className="v-inline-upload__info">
          <InfoCircleOutlined />
          <span>
            <strong>Twilio:</strong> Go to{' '}
            <a href="https://www.twilio.com/en-us/sip-trunking/pricing/us" target="_blank" rel="noopener noreferrer">
              Twilio SIP Trunking Pricing
            </a>
            {' '}and click{' '}
            <strong>
              {importType === 'phone_number_price'
                ? '"Download origination prices (CSV)"'
                : '"Download termination prices (CSV)"'}
            </strong>
          </span>
        </div>

        {importType === 'rate' && (
          <>
            <div className="v-inline-upload__info">
              <InfoCircleOutlined />
              <span>
                <strong>WorldCall:</strong> Download CSV from{' '}
                <a href="https://www.termination.worldcall.be/main.php/rate/?vendor=worldcall&tab=standard&currency=usd" target="_blank" rel="noopener noreferrer">
                  WorldCall Termination Rates
                </a>
              </span>
            </div>
            <div className="v-inline-upload__info">
              <InfoCircleOutlined />
              <span>
                <strong>DIDWW:</strong> CSV link is sent via email
              </span>
            </div>
            <div className="v-inline-upload__info">
              <InfoCircleOutlined />
              <span>
                <strong>VoxBeam:</strong> Download CSV from VoxBeam admin panel
              </span>
            </div>
          </>
        )}

        <Form.Item className="v-inline-upload__submit">
          <Button
            className="v-btn v-btn-primary"
            type="primary"
            htmlType="submit"
            loading={uploading}
            disabled={!file}
          >
            Upload
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default _InlineUploadForm;
